import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { Lead } from 'types'

interface LeadDeleteItem {
  lead_uuid: string
}

const batchDeleteLeads = async (leads: LeadDeleteItem[]): Promise<any> => {
  const url = `${baseUrl}/aws/leads/batch-delete`
  try {
    const response = await axios.post(url, { leads })
    if (response.status !== 200) throw new Error('Network response was not ok')
    return response.data
  } catch (error) {
    console.error('Failed to batch delete leads:', error)
    throw error
  }
}

export const useBatchDeleteLeads = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: batchDeleteLeads,
    onMutate: async (leadsToDelete: LeadDeleteItem[]) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['leads'] })

      // Snapshot the previous value
      const previousLeads = queryClient.getQueryData<Lead[]>(['leads'])

      // Optimistically update to the new value
      if (previousLeads) {
        const idsToDelete = new Set(leadsToDelete.map(l => l.lead_uuid))
        queryClient.setQueryData<Lead[]>(
          ['leads'],
          previousLeads.filter(lead => !idsToDelete.has(lead.lead_uuid))
        )
      }

      // Return context with the previous value
      return { previousLeads }
    },
    onSuccess: () => {
      // Invalidate and refetch the leads query
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
    onError: (error, _variables, context) => {
      console.error('[useBatchDeleteLeads] Error batch deleting leads:', error)
      // Rollback to the previous value on error
      if (context?.previousLeads) {
        queryClient.setQueryData(['leads'], context.previousLeads)
      }
    },
  })
}
